import { useContext, useEffect, useRef } from 'react';
import { LanguageContext } from '../App';
import { normalizeTrustpilotDomain, trustpilotReviewUrl, trustpilotWidgetLocale } from '../utils/trustpilotUtils';

const SCRIPT_SRC = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
/** Mini TrustScore — star rating + score from Trustpilot (live data). */
const TEMPLATE_MINI_TRUSTSCORE = '53aa8912dec7e10d38f59f32';

let scriptLoadPromise = null;

function loadTrustpilotBootstrap() {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.Trustpilot) return Promise.resolve();
  if (scriptLoadPromise) return scriptLoadPromise;

  const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
  if (existing) {
    scriptLoadPromise = new Promise((resolve) => {
      if (window.Trustpilot) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
    });
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Trustpilot widget script failed to load'));
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
}

/**
 * Official Trustpilot TrustBox — renders real star rating from Trustpilot CDN.
 * Requires trustpilot_business_unit_id from site settings (auto-resolved from domain on the backend).
 */
const TrustpilotWidget = ({
  businessUnitId,
  reviewDomain,
  templateId = TEMPLATE_MINI_TRUSTSCORE,
  theme = 'dark',
  height = '24px',
  className = '',
}) => {
  const { language } = useContext(LanguageContext);
  const widgetRef = useRef(null);
  const unitId = String(businessUnitId || '').trim();
  const domain = normalizeTrustpilotDomain(reviewDomain);
  const reviewUrl = trustpilotReviewUrl(domain, language);
  const locale = trustpilotWidgetLocale(language);
  const reviewLang = locale.split('-')[0];

  useEffect(() => {
    if (!unitId || !widgetRef.current) return undefined;

    let cancelled = false;
    loadTrustpilotBootstrap()
      .then(() => {
        if (cancelled || !widgetRef.current || !window.Trustpilot) return;
        window.Trustpilot.loadFromElement(widgetRef.current, true);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [unitId, templateId, theme, height, locale, reviewLang, reviewUrl]);

  if (!unitId) return null;

  return (
    <div
      ref={widgetRef}
      className={`trustpilot-widget ${className}`.trim()}
      data-locale={locale}
      data-template-id={templateId}
      data-businessunit-id={unitId}
      data-style-height={height}
      data-style-width="100%"
      data-theme={theme}
      data-review-languages={reviewLang}
    >
      {reviewUrl ? (
        <a href={reviewUrl} target="_blank" rel="noopener noreferrer" className="text-white/50 text-sm">
          Trustpilot
        </a>
      ) : null}
    </div>
  );
};

export default TrustpilotWidget;
